import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Collapse from '@kunukn/react-collapse'
import cx from 'classnames'

import { collapsibleClick } from '@cred/react-analytics//events'
import { withSnowplowContexts } from '@cred/react-analytics/SnowplowContextManager'

import { PartnerLogo } from 'components/partner-logo'
import { fireGtmEvent } from 'utils/gtm'
import RateTableCtaButton from 'components/RateTableCtaButton'
import { useViewport } from 'components/hooks'
import { addTrackingParamsToUrl } from 'utils/urls'
import mapLenderSlagToTrackingName from 'utils/mapLenderSlagToTrackingName'
import { PERSONAL } from 'common/const/verticalNames'
import RateTableDisclosureButton from 'components/RateTableDisclosureButton'
import RateTableAprValue from 'components/RateTableAprValue'
import RateTableLoanTermValue from 'components/RateTableLoanTermValue'
import { NO_OFFERS } from 'common/const/otherOptions'

import styles from './styles.module.scss'
import disclaimers from './disclaimers'
import noOffersDisclaimers from './no-offers-disclaimers'

const LenderRate = ({
  trackSnowplowEvent,
  lender,
  widgetContext,
  ctaUrl,
  shouldDarkenBorder,
  blank = true,
  otherOptions,
}) => {
  const [open, setOpen] = useState(false)
  const { xSmall } = useViewport()

  const ctaUrlWithTrackingParams = addTrackingParamsToUrl({
    widgetContext,
    lenderSlug: mapLenderSlagToTrackingName(lender.slug, PERSONAL),
    url: ctaUrl,
  })

  const toggleLendersDisclosuresModal = () => {
    trackSnowplowEvent(collapsibleClick, {
      text: 'Disclaimers',
      action: open ? 'collapse' : 'expand',
    })

    setOpen((prevOpen) => !prevOpen)
  }

  const onClickCta = () => {
    const lenderObj = { lender: lender.name }

    fireGtmEvent('ctaClick', Object.assign(lenderObj, widgetContext))
  }

  const disclosureButton = (
    <RateTableDisclosureButton
      testid="toggle-button"
      open={open}
      onClick={toggleLendersDisclosuresModal}
      className={styles.disclaimerWrapper}
    />
  )
  const isNonOffer = otherOptions?.includes(NO_OFFERS)
  const disclaimer = disclaimers[lender.slug]
  const noOfferDisclaimerFromList = noOffersDisclaimers[lender.slug]
  const currentDisclaimer = isNonOffer ? noOfferDisclaimerFromList || disclaimer : disclaimer

  return (
    <div
      data-test-id={`rateTableTile_${lender.rateSlug}`}
      className={cx(styles.panel, {
        [styles.darkenedBorder]: shouldDarkenBorder,
      })}
      color="accent"
    >
      <div className="pure-g">
        <div className="pure-u-1 pure-u-md-1-4 pure-u-sm-1-1 mb-16">
          <PartnerLogo className={lender.slug} partner={lender.slug} />
          {!xSmall && disclosureButton}
        </div>
        <RateTableAprValue
          minRate={lender.minFixedRate}
          maxRate={lender.maxFixedRate}
          withBottomBorder
          className="pb-16"
        />
        <RateTableLoanTermValue minLoanTerm={lender.minLoanTerm} maxLoanTerm={lender.maxLoanTerm} />
        <div className="pure-u-1 pure-u-md-1-4 pure-u-sm-1-3 mt-16 mt-sm-0">
          <RateTableCtaButton
            containerClassName={styles.cta}
            data-testid="cta-button"
            target={blank ? '_blank' : '_self'}
            transparent={!xSmall}
            href={ctaUrlWithTrackingParams}
            onClick={onClickCta}
            fullWidth
            btnText="Check Rates"
          />

          {xSmall && disclosureButton}
        </div>
        <div className="pure-u-1">
          <Collapse data-testid="collapse-component" className={styles.dropdown} isOpen={open}>
            <div className={`disclaimer mt-24 mb-16 ${styles.disclosure}`}>{currentDisclaimer}</div>
          </Collapse>
        </div>
      </div>
    </div>
  )
}

LenderRate.propTypes = {
  ctaUrl: PropTypes.string,
  lender: PropTypes.object,
  widgetContext: PropTypes.object,
  trackSnowplowEvent: PropTypes.func,
  shouldDarkenBorder: PropTypes.bool,
  blank: PropTypes.bool,
  otherOptions: PropTypes.instanceOf(Array),
}

export { LenderRate }

export default withSnowplowContexts(LenderRate)
